/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { loginStyles } from '@constants/mui-theme';
import { validateEmail, validatePasswords } from '@utils/auth-utils';

import ResetRequest from './reset-request';
import PasswordCreate from './password-create';
import Success from './success';

import {
  authSetUserField,
  sendPasswordChangeRequest,
  sendNewPasswords,
  getPasswordChangeFormData
} from '@actions/auth-actions';

import '../layouts/not-authenticated/not-authenticated.scss';

const PasswordRecovery = () => {
  const dispatch = useDispatch();
  const { token, userPk, onboarding } = useParams();
  const {
    email,
    emailErrorText,
    fullname,
    passwordErrorText,
    passwordChangeFormData,
    passwordChangeData,
    requestedReset
  } = useSelector(state => state.auth.user);
  const [password, setPassword] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');

  const handleFormChange = useCallback(event => {
    const { name, value } = event.target;
    if (name !== 'password' && name !== 'passwordTwo') {
      dispatch(authSetUserField(name, value));
    } else
      if (name === 'password') {
        setPassword(value);
      } else {
        setPasswordTwo(value);
      }
  }, [dispatch, setPassword, setPasswordTwo]);

  const handlePasswordRequest = useCallback(() => {
    const error = validateEmail(email) ? '' : 'Invalid email address';
    dispatch(authSetUserField('emailErrorText', error));
    if (error === '') {
      dispatch(sendPasswordChangeRequest(email));
    }
  }, [dispatch, email]);

  const handleNewPasswordSent = useCallback(() => {
    const error = validatePasswords(password, passwordTwo);
    dispatch(authSetUserField('passwordErrorText', error));
    if (error === '') {
      dispatch(sendNewPasswords({
        userPk,
        token,
        new_password1: password,
        new_password2: passwordTwo,
        ...(onboarding && {name: fullname})
      }));
    }
  }, [dispatch, userPk, token, onboarding, fullname, password, passwordTwo]);

  const handleFormSubmit = useCallback(event => {
    event.preventDefault();
    if (token && userPk) {
      handleNewPasswordSent();
    } else {
      handlePasswordRequest();
    }
  }, [token, userPk, handleNewPasswordSent, handlePasswordRequest]);

  const isOnboarding = onboarding === 'onboarding';

  const validatedPasswordChangeForm = useMemo(() => {
    if (passwordChangeFormData.userPk === userPk && passwordChangeFormData.token === token) {
      return passwordChangeFormData;
    }
    return null;
  }, [userPk, token, passwordChangeFormData]);
  useEffect(() => {
    if (userPk && token && !validatedPasswordChangeForm) {
      dispatch(getPasswordChangeFormData(userPk, token));
    }
  }, [dispatch, userPk, token, validatedPasswordChangeForm]);

  const passwordErrors = useMemo(() => {
    let errors = [];
    if (passwordErrorText) {
      errors = [...errors, passwordErrorText];
    }
    if (passwordChangeData.status && passwordChangeData.status >= 400) {
      errors = [...errors, ...(passwordChangeData.data?.new_password2 || null)];
    }
    return errors.flatMap((error, index) => (index === errors.length - 1) ? [error] : [error, <br key={index}/>]);
  }, [passwordErrorText, passwordChangeData]);

  if (passwordChangeData.status !== 201) {
    return (
      <div styleName="not-authenticated-container">
        {(token && userPk)
          ? <PasswordCreate
            formChange={handleFormChange}
            formSubmit={handleFormSubmit}
            isOnboarding={isOnboarding}
            formData={validatedPasswordChangeForm}
            passwordErrorText={passwordErrors}
            fullname={fullname}
            password={password}
            passwordTwo={passwordTwo} />
          : <ResetRequest
            emailErrorText={emailErrorText}
            formChange={handleFormChange}
            formSubmit={handleFormSubmit}
            requestedResetStatus={requestedReset.status} />
        }
        <div styleName="auth-section">
          <div styleName="link-text">
            Already have an account?<br /><Link style={loginStyles.links} to={'/login'}>Login</Link>
            <br/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div styleName="not-authenticated-container authentication-success-container">
      <Success
        isOnboarding={isOnboarding} />
      <div styleName="auth-section">
        <div>
          <Link style={loginStyles.successLinks} to={'/login'}>Login Now</Link>
        </div>
      </div>
    </div>
  );
};

export default memo(PasswordRecovery);
